// =============================================================================
// Library Batches Page — upload batch list with status filter
// =============================================================================

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardHeader, Select, Spinner, Table } from '@tkpod/ui';
import type { Column } from '@tkpod/ui';
import { LibraryUploadBatchStatus } from '@tkpod/shared';
import { fetchBatches, libraryKeys, type BatchListItem } from './api';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...Object.values(LibraryUploadBatchStatus).map((s) => ({ value: s, label: s })),
];

function batchVariant(s: string): 'success' | 'warning' | 'default' {
  if (s === 'COMPLETED') return 'success';
  if (s === 'PROCESSING' || s === 'REVIEW') return 'warning';
  return 'default';
}

export function LibraryBatchesPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const params = useMemo(() => ({ status, page }), [status, page]);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    p.set('page', String(page));
    return p.toString();
  }, [status, page]);

  const batchesQuery = useQuery({ queryKey: libraryKeys.batches(params), queryFn: () => fetchBatches(qs) });
  const items = batchesQuery.data?.items ?? [];
  const total = batchesQuery.data?.total ?? 0;
  const pageSize = batchesQuery.data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const columns: Column<BatchListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Batch',
        render: (b) => (
          <button type="button" onClick={() => navigate(`/library/uploads/${b.id}`)} className="font-medium text-brand-400 hover:underline">
            {b.name}
          </button>
        ),
      },
      { key: 'status', header: 'Status', render: (b) => <Badge variant={batchVariant(b.status)} size="sm">{b.status}</Badge> },
      { key: 'progress', header: 'Progress', render: (b) => <span className="text-text-secondary">{b.readyCount + b.failedCount}/{b.totalCount} ({b.failedCount} failed)</span> },
      { key: 'createdBy', header: 'Uploaded by', render: (b) => <span className="text-text-secondary">{b.createdByName ?? '—'}</span> },
      { key: 'createdAt', header: 'Created', render: (b) => <span className="text-text-tertiary">{new Date(b.createdAt).toLocaleString()}</span> },
    ],
    [navigate],
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Upload batches"
          description="Review, retry and finish upload batches."
          action={<Button size="sm" onClick={() => navigate('/library/upload')}>+ Upload</Button>}
        />
        <div className="mt-4 max-w-xs">
          <Select options={STATUS_OPTIONS} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} />
        </div>
        <div className="mt-4">
          {batchesQuery.isLoading ? (
            <div className="flex justify-center py-16"><Spinner size="lg" label="Loading batches…" /></div>
          ) : (
            <Table<BatchListItem> columns={columns} data={items} rowKey={(b) => b.id} emptyMessage="No batches yet." />
          )}
        </div>
        <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
          <span>{total} batch(es)</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span>Page {page} / {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
